import React, { useState, useEffect, useRef } from "react";
import {
  Upload,
  Database,
  Save,
  RefreshCcw,
  CheckCircle,
  FileText,
  Trash2,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  Network,
  AlertCircle
} from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const TAB_ORDER = ["connection", "ingest"];

const KnowledgeGraph = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState("connection");
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const DOCS_PER_PAGE = 10;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const [vectorConfig, setVectorConfig] = useState({
    strategy: "RecursiveChunker",
    chunk_size: 2000,
    chunk_overlap: 200,
    search_top_k: 5,
    search_threshold: 0.5,
    search_type: "hybrid",
    knowledge_source: "vector",
    neo4j_uri: "bolt://localhost:7687",
    neo4j_user: "neo4j",
    neo4j_password: "",
    neo4j_database: "neo4j",
    params: {}
  });

  useEffect(() => {
    loadDocuments();
    fetchVectorConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAiAgentId]);

  const loadDocuments = async () => {
    try {
      const response = await ApiClient.getDocuments();
      const docs = response.documents || [];
      const formattedDocs = docs
        .filter((doc) => (doc.original_name || "").toLowerCase().endsWith(".cypher"))
        .map((doc) => ({
          ...doc,
          name: doc.original_name,
          dbId: doc.id
        }));
      setDocuments(formattedDocs);
    } catch (error) {
      console.error("Failed to load Cypher documents:", error);
    }
  };

  const fetchVectorConfig = async () => {
    try {
      const response = await ApiClient.request("/api/vector/config");
      if (response) {
        setVectorConfig({
          ...response,
          params: response.params || {}
        });
      }
    } catch (error) {
      console.error("Error fetching Neo4j config:", error);
    }
  };

  const saveVectorConfig = async () => {
    setConfigSaving(true);
    try {
      await ApiClient.request("/api/vector/config", {
        method: "POST",
        body: JSON.stringify(vectorConfig)
      });
      showToast("Knowledge source configuration updated successfully!", "success");
    } catch (error) {
      console.error("Error saving Neo4j config:", error);
      showToast("Failed to save configuration", "error");
    } finally {
      setConfigSaving(false);
    }
  };

  const handleFiles = async (files) => {
    const validFiles = files.filter((file) => file.name.toLowerCase().endsWith(".cypher"));
    if (validFiles.length === 0) {
      showToast("Please upload valid .cypher files only", "error");
      return;
    }

    setUploading(true);
    try {
      const response = await ApiClient.uploadDocuments(validFiles, {});
      const uploadedFiles = response.files || [];
      showToast(`Uploaded ${validFiles.length} file(s). Executing queries...`, "info");
      
      uploadedFiles.forEach((file) => {
        pollDocumentStatus(file.id, file.filename || file.original_name);
      });
      loadDocuments();
    } catch (error) {
      console.error("Upload failed:", error);
      showToast(error.message || "Failed to upload Cypher file", "error");
      setUploading(false);
    }
  };

  const pollDocumentStatus = async (docId, filename) => {
    const checkStatus = async () => {
      try {
        const statusResponse = await ApiClient.request(`/documents/status/${docId}`);
        if (statusResponse.status === "completed") {
          showToast(`Cypher file '${filename}' executed successfully!`, "success");
          await loadDocuments();
          setUploading(false);
        } else if (statusResponse.status === "failed") {
          showToast(`Cypher execution failed for '${filename}': ${statusResponse.status_detail || "Unknown error"}`, "error", 10000);
          await loadDocuments();
          setUploading(false);
        } else {
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        console.error("Status polling failed:", error);
        setTimeout(checkStatus, 2000);
      }
    };
    setTimeout(checkStatus, 100);
  };

  const confirmDeleteDoc = (doc) => {
    setDocToDelete(doc);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!docToDelete) return;
    setIsDeleting(true);
    try {
      await ApiClient.deleteDocument(docToDelete.dbId);
      showToast("Cypher history record deleted", "success");
      loadDocuments();
    } catch (error) {
      console.error("Delete failed:", error);
      showToast("Failed to delete record", "error");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setDocToDelete(null);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-6">
        {/* Header matching Knowledge Base (DocumentUpload.js) */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">
                Knowledge Graph
              </h1>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Connect to Neo4j to build semantic relationship networks and enhance your agent with graph-based context.
            </p>
          </div>

          <div className="self-start sm:self-auto flex flex-col xl:flex-row items-start xl:items-center gap-4">
            {/* Minimal Tab Switcher */}
            <div className="flex p-1 bg-zinc-100/80 rounded-xl border border-zinc-200/60 w-full sm:w-auto overflow-x-auto custom-scrollbar">
              <button
                onClick={() => setActiveTab("connection")}
                className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  activeTab === "connection"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <Database size={13} />
                <span>Connection</span>
              </button>
              <button
                onClick={() => setActiveTab("ingest")}
                className={`flex-1 sm:flex-initial px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 whitespace-nowrap ${
                  activeTab === "ingest"
                    ? "bg-white text-zinc-900 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <Upload size={13} />
                <span>Cypher Ingestion</span>
              </button>
            </div>

            {/* Active / Inactive Knowledge Source Toggle */}
            <div className="flex items-center gap-2.5 h-auto sm:h-[38px]">
              <span
                className={`text-xs font-medium ${
                  vectorConfig.knowledge_source === "graph" ? "text-zinc-900 font-semibold" : "text-zinc-400"
                }`}
              >
                {vectorConfig.knowledge_source === "graph" ? "Active" : "Inactive"}
              </span>

              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={vectorConfig.knowledge_source === "graph"}
                  onChange={async () => {
                    const newSource = vectorConfig.knowledge_source === "graph" ? "vector" : "graph";
                    const updated = { ...vectorConfig, knowledge_source: newSource };
                    setVectorConfig(updated);

                    setConfigSaving(true);
                    try {
                      await ApiClient.request("/api/vector/config", {
                        method: "POST",
                        body: JSON.stringify(updated)
                      });
                      showToast(
                        `Knowledge source updated to ${
                          newSource === "graph" ? "Knowledge Graph" : "Vector Search"
                        }!`,
                        "success"
                      );
                    } catch (e) {
                      showToast("Failed to update knowledge source", "error");
                    } finally {
                      setConfigSaving(false);
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-white border-2 border-zinc-200 rounded-full peer peer-checked:border-zinc-900 transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-zinc-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-zinc-900"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Sliding Tab Track */}
        <div className="overflow-hidden w-full pt-2">
          <div
            className="flex transition-transform duration-300 ease-out items-start"
            style={{
              transform: `translateX(-${(TAB_ORDER.indexOf(activeTab) * 100) / 2}%)`,
              width: "200%"
            }}
          >
            {/* Panel 1: Connection Settings */}
            <div className="w-1/2 shrink-0 sm:px-2">
              <div className="bg-white rounded-2xl border border-zinc-100 shadow-xs overflow-hidden min-h-[calc(100vh-185px)] flex flex-col">
                {/* Header Banner matching Knowledge Base settings card */}
                <div className="bg-zinc-50/50 p-6 border-b border-zinc-100">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 px-4">
                    <div className="flex items-center gap-3">
                      <Database className="text-zinc-700 shrink-0" size={20} />
                      <div>
                        <h3 className="text-sm font-semibold text-zinc-900 tracking-tight">
                          Database Credentials
                        </h3>
                        <p className="text-xs text-zinc-500">
                          Specify Neo4j connection parameters
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={saveVectorConfig}
                      disabled={configSaving}
                      className="flex items-center gap-2 px-4 py-2 sm:py-2.5 bg-zinc-900 text-white rounded-xl text-xs font-medium hover:bg-zinc-800 transition-all shadow-sm active:scale-95 disabled:opacity-40"
                    >
                      {configSaving ? (
                        <RefreshCcw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {configSaving ? "Deploying..." : "Deploy Settings"}
                    </button>
                  </div>
                </div>

                {/* Main Form Fields */}
                <div className="p-8 lg:p-10 space-y-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-zinc-700 block">
                          Neo4j URI
                        </label>
                        <input
                          type="text"
                          placeholder="bolt://localhost:7687"
                          value={vectorConfig.neo4j_uri || ""}
                          onChange={(e) =>
                            setVectorConfig({ ...vectorConfig, neo4j_uri: e.target.value })
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-zinc-200 rounded-xl text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 transition-all"
                        />
                        <p className="text-[9px] text-zinc-400 font-medium">
                          Supports Bolt protocol: <span className="font-mono text-zinc-600">bolt://</span> or <span className="font-mono text-zinc-600">neo4j://</span>
                        </p>
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-medium text-zinc-700 block">
                          Neo4j Database
                        </label>
                        <input
                          type="text"
                          placeholder="neo4j"
                          value={vectorConfig.neo4j_database || ""}
                          onChange={(e) =>
                            setVectorConfig({ ...vectorConfig, neo4j_database: e.target.value })
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-zinc-200 rounded-xl text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 transition-all"
                        />
                        <p className="text-[9px] text-zinc-400 font-medium">
                          Default database instance is usually <span className="font-mono text-zinc-600">neo4j</span>
                        </p>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-zinc-700 block">
                          Neo4j Username
                        </label>
                        <input
                          type="text"
                          placeholder="neo4j"
                          value={vectorConfig.neo4j_user || ""}
                          onChange={(e) =>
                            setVectorConfig({ ...vectorConfig, neo4j_user: e.target.value })
                          }
                          className="w-full px-3.5 py-2.5 bg-white border border-zinc-200 rounded-xl text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 transition-all"
                        />
                        <p className="text-[9px] text-zinc-400 font-medium">
                          Administrative database user account
                        </p>
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-medium text-zinc-700 block">
                          Neo4j Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter Neo4j Password"
                            value={vectorConfig.neo4j_password || ""}
                            onChange={(e) =>
                              setVectorConfig({ ...vectorConfig, neo4j_password: e.target.value })
                            }
                            className="w-full px-3.5 py-2.5 pr-12 bg-white border border-zinc-200 rounded-xl text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 transition-all"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        <p className="text-[9px] text-zinc-400 font-medium">
                          Secured password required to execute Cypher queries
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Panel 2: Cypher Ingestion */}
            <div className="w-1/2 shrink-0 sm:px-2 border-l border-zinc-200/50 min-h-[calc(100vh-185px)]">
              <div className="grid gap-8">
                {/* Upload Area matching Knowledge Base */}
                <div
                  className={`bg-white rounded-2xl p-6 sm:p-8 border-2 border-dashed transition-all duration-300 relative overflow-hidden group ${
                    isDragging
                      ? "border-zinc-900 bg-zinc-50 ring-4 ring-zinc-900/5"
                      : "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50/30"
                  } cursor-pointer shadow-xs`}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    handleFiles(Array.from(e.dataTransfer.files));
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={(e) => handleFiles(Array.from(e.target.files))}
                    className="hidden"
                    accept=".cypher"
                  />
                  <div className="flex flex-col items-center justify-center py-4 text-center">
                    <Upload className="text-zinc-400 shrink-0" />
                    <h3 className="text-sm sm:text-base font-semibold text-zinc-900 mb-1 tracking-tight">
                      {isDragging ? "Drop Cypher File" : "Ingest Cypher File"}
                    </h3>
                    <p className="text-zinc-500 text-xs mb-1 max-w-xs leading-relaxed">
                      Drag and drop .cypher files or click to browse
                    </p>
                    <div className="flex flex-wrap justify-center gap-1.5 mt-3">
                      <span className="px-2.5 py-0.5 bg-zinc-100 text-zinc-600 rounded-md text-[11px] font-medium">
                        .cypher
                      </span>
                    </div>

                    {uploading && (
                      <div className="mt-4 flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-lg shadow-xs text-xs font-medium">
                        <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                        <span>Running Cypher Queries...</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Pipeline Info Card */}
                <div className="bg-white rounded-2xl p-6 border border-zinc-100 shadow-xs">
                  <div className="flex items-center gap-3 mb-4">
                    <Network className="text-zinc-700 shrink-0" size={20} />
                    <div>
                      <h3 className="text-sm font-semibold text-zinc-900 tracking-tight">
                        Cypher Seed Engine
                      </h3>
                      <p className="text-xs text-zinc-500">
                        Direct graph database population
                      </p>
                    </div>
                  </div>
                  <p className="text-[11px] text-zinc-500 font-medium leading-relaxed">
                    Upload raw <span className="font-mono text-zinc-800">.cypher</span> files containing <span className="font-mono text-zinc-800">CREATE</span>, <span className="font-mono text-zinc-800">MERGE</span>, or schema statements. Queries are validated and executed in sequential transaction batches directly against your configured Neo4j database instance.
                  </p>
                </div>

                {/* Ingestion History List matching Knowledge Base */}
                <div className="bg-white rounded-2xl p-6 border border-zinc-100 shadow-sm">
                  <div className="flex items-center justify-between mb-6 px-1">
                    <h3 className="text-sm font-semibold text-zinc-900">
                      Ingestion History
                    </h3>
                    <span className="bg-zinc-100 text-zinc-600 px-2.5 py-1 rounded-lg text-xs font-medium">
                      {documents.length} Cypher Files Total
                    </span>
                  </div>

                  {documents.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText size={32} className="text-zinc-200 mx-auto mb-3" />
                      <p className="text-xs font-medium text-zinc-400">
                        No Cypher files uploaded yet
                      </p>
                      <p className="text-[9px] text-zinc-300 mt-1">
                        Upload .cypher scripts above to populate your graph database.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="grid gap-2 mb-4 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
                        {documents
                          .slice((currentPage - 1) * DOCS_PER_PAGE, currentPage * DOCS_PER_PAGE)
                          .map((doc, index) => (
                            <div
                              key={doc.dbId || index}
                              className="flex items-center justify-between p-2.5 bg-zinc-50/50 rounded-xl border border-zinc-100 hover:border-zinc-200 transition-all duration-300 group"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <FileText className="text-zinc-400 shrink-0" size={18} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-zinc-900 truncate pr-4 tracking-tight">
                                    {doc.name}
                                  </p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    {doc.status === "processing" ? (
                                      <span className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-md">
                                        <RefreshCcw size={8} className="animate-spin" /> Processing
                                      </span>
                                    ) : doc.status === "failed" ? (
                                      <span className="flex items-center gap-1.5 text-[11px] font-medium text-rose-700 bg-rose-50 border border-rose-100 px-2 py-0.5 rounded-md">
                                        <AlertCircle size={8} /> Error: {doc.status_detail || "Execution Failure"}
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-md">
                                        <CheckCircle size={8} /> Executed Successfully
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  onClick={() => confirmDeleteDoc(doc)}
                                  className="p-2 text-zinc-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                  title="Delete record"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>

                      {/* Pagination Controls */}
                      {Math.ceil(documents.length / DOCS_PER_PAGE) > 1 && (
                        <div className="flex items-center justify-between pt-4 border-t border-zinc-50">
                          <span className="text-xs font-medium text-zinc-500 px-2">
                            Page {currentPage} of {Math.ceil(documents.length / DOCS_PER_PAGE)}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              className="p-1.5 rounded-lg border border-zinc-100 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                            >
                              <ChevronLeft size={14} />
                            </button>
                            <button
                              onClick={() =>
                                setCurrentPage((prev) =>
                                  Math.min(prev + 1, Math.ceil(documents.length / DOCS_PER_PAGE))
                                )
                              }
                              disabled={currentPage === Math.ceil(documents.length / DOCS_PER_PAGE)}
                              className="p-1.5 rounded-lg border border-zinc-100 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                            >
                              <ChevronRight size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDelete}
          title="Delete Cypher Record"
          message="Are you sure you want to delete this Cypher ingestion record? This will not undo database changes but removes the file record."
          isDeleting={isDeleting}
        />
      </div>
    </div>
  );
};

export default KnowledgeGraph;
